<template>
    <div class="card mt-3">
        <div class="card-header">
            <h6 class="d-inline">Tag {{ tag.tagId }}</h6>
            <button
                class="btn btn-sm btn-light float-right d-inline py-0"
                @click="removeTag">
                &times; Remove
            </button>
        </div>
        <div class="card-body">
            <div class="row">
                <div class="form-group row col-6">
                    <div class="col-4">
                        <label class="col-form-label col-form-label-sm">Tag Name</label>
                    </div>
                    <div class="col-8">
                        <input
                            type="text"
                            class="form-control form-control-sm"
                            :class="{ 'is-invalid': tagNameError }"
                            v-model="tag.tagName"
                            @change="validateTagName" />
                        <div class="invalid-feedback">Max. 15 alphanumeric characters only; cannot begin with numbers</div>
                    </div>
                </div>
                <div class="form-group row col-6">
                    <div class="col-4">
                        <label class="col-form-label col-form-label-sm">Tag Bit/Byte Offset</label>
                    </div>
                    <div class="col-8">
                        <input
                            type="text"
                            class="form-control form-control-sm"
                            :class="{ 'is-invalid': tagAddressOffsetError }"
                            v-model="tag.tagAddressOffset"
                            @change="validateTagAddressOffset" />
                        <div class="invalid-feedback">Offset exceeds max. range</div>
                    </div>
                </div>

                <div class="form-group row col-6">
                    <div class="col-4">
                        <label class="col-form-label col-form-label-sm">Tag Datatype</label>
                    </div>
                    <div class="col-8">
                        <select
                            class="form-control form-control-sm"
                            :class="{ 'is-invalid': tagDatatypeError }"
                            v-model="tag.tagDatatype"
                            @change="suggestTagAddressLength">
                            <option
                                v-for="datatypeOption in datatypeOptions"
                                :key="datatypeOption.value"
                                :value="datatypeOption.value">
                                {{ datatypeOption.text }}
                            </option>
                        </select>
                        <div class="invalid-feedback">Required field. Please select one</div>
                    </div>
                </div>
                <div class="form-group row col-6">
                    <div class="col-4">
                        <label class="col-form-label col-form-label-sm">Tag Address Length</label>
                    </div>
                    <div class="col-8">
                        <input
                            type="text"
                            class="form-control form-control-sm"
                            :class="{ 'is-invalid': tagAddressLengthError }"
                            v-model="tag.tagAddressLength"
                            @change="validateTagAddressLength"
                            disabled />
                    </div>
                </div>
                <div class="form-group row col-6">
                    <div class="col-4">
                        <label class="col-form-label col-form-label-sm">Linear Scaling Coefficient A</label>
                    </div>
                    <div class="col-8">
                        <input
                            type="text"
                            class="form-control form-control-sm"
                            :class="{ 'is-invalid': tagLinearScalingCoefficientAError }"
                            v-model="tag.tagLinearScalingCoefficientA"
                            @change="validateTagLinearScalingCoefficientA" />
                        <div class="invalid-feedback">Numbers (except 0) only</div>
                    </div>
                </div>
                <div class="form-group row col-6">
                    <div class="col-4">
                        <label class="col-form-label col-form-label-sm">Linear Scaling Offset B</label>
                    </div>
                    <div class="col-8">
                        <input
                            type="text"
                            class="form-control form-control-sm"
                            :class="{ 'is-invalid': tagLinearScalingOffsetBError }"
                            v-model="tag.tagLinearScalingOffsetB"
                            @change="validateTagLinearScalingOffsetB" />
                        <div class="invalid-feedback">Numbers only</div>
                    </div>
                </div>
                <div class="form-group row col-6">
                    <div class="col-4">
                        <label class="col-form-label col-form-label-sm">Alert Flag</label>
                    </div>
                    <div class="col-8">
                        <select
                            class="form-control form-control-sm"
                            v-model="tag.tagAlertFlag">
                            <option
                                v-for="alertFlagOption in alertFlagOptions"
                                :value="alertFlagOption.value"
                                :key="alertFlagOption.value">
                                {{ alertFlagOption.text }}
                            </option>
                        </select>
                    </div>
                </div>
                <div class="form-group row col-6" v-if="registerType === 'analog'">
                    <div class="col-4">
                        <label class="col-form-label col-form-label-sm">Minimum Threshold</label>
                    </div>
                    <div class="col-8">
                        <input
                            type="text"
                            class="form-control form-control-sm"
                            :class="{ 'is-invalid': tagMinimumThresholdError }"
                            v-model="tag.tagMinimumThreshold"
                            @change="validateTagMinimumThreshold" />
                        <div class="invalid-feedback">Numbers only</div>
                    </div>
                </div>
                <div class="form-group row col-6" v-if="registerType === 'analog'">
                    <div class="col-4">
                        <label class="col-form-label col-form-label-sm">Maximum Threshold</label>
                    </div>
                    <div class="col-8">
                        <input
                            type="text"
                            class="form-control form-control-sm"
                            :class="{ 'is-invalid': tagMaximumThresholdError }"
                            v-model="tag.tagMaximumThreshold"
                            @change="validateTagMaximumThreshold" />
                        <div class="invalid-feedback">Numbers only</div>
                    </div>
                </div>
                <div class="form-group row col-6">
                    <div class="col-4">
                        <label class="col-form-label col-form-label-sm">Mobile Number</label>
                    </div>
                    <div class="col-8">
                        <input
                            type="text"
                            class="form-control form-control-sm"
                            :class="{ 'is-invalid': tagMobileNumberError }"
                            v-model="tag.tagMobileNumber"
                            @change="validateTagMobileNumber" />
                        <div class="invalid-feedback">Numbers only</div>
                    </div>
                </div>
                <div class="form-group row col-6">
                    <div class="col-4">
                        <label class="col-form-label col-form-label-sm">Enable Critical Tag?</label>
                    </div>
                    <div class="col-8">
                        <select
                            class="form-control form-control-sm"
                            v-model="tag.tagCriticalTag">
                            <option value="0">Normal</option>
                            <option value="1">Critical</option>
                        </select>
                    </div>
                </div>
                <div class="form-group row col-6">
                    <div class="col-4">
                        <label class="col-form-label col-form-label-sm">Enable Critical Alarm?</label>
                    </div>
                    <div class="col-8">
                        <select
                            class="form-control form-control-sm"
                            v-model="tag.tagCriticalAlarm">
                            <option value="0">Disable</option>
                            <option value="1">Enable</option>
                        </select>
                    </div>
                </div>
                <div class="form-group row col-6">
                    <div class="col-4">
                        <label class="col-form-label col-form-label-sm">Store data as Float?</label>
                    </div>
                    <div class="col-8">
                        <select
                            class="form-control form-control-sm"
                            v-model="tag.tagStoreAsFloat">
                            <option value="1">Yes</option>
                            <option value="0">No</option>
                        </select>
                    </div>
                </div>
                <div class="form-group row col-6" v-if="loggingMethod === 'cumulocity'">
                    <div class="col-4">
                        <label class="col-form-label col-form-label-sm">Unit of Measurement</label>
                    </div>
                    <div class="col-8">
                        <input
                            type="text"
                            class="form-control form-control-sm"
                            v-model="tag.tagUnitOfMeasurement" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            tagNameError: false,
            tagAddressOffsetError: false,
            tagDatatypeError: false,
            tagAddressLengthError: false,
            tagLinearScalingCoefficientAError: false,
            tagLinearScalingOffsetBError: false,
            tagMinimumThresholdError: false,
            tagMaximumThresholdError: false,
            tagMobileNumberError: false
        }
    },
    computed: {
        registerType() {
            if(this.query.functionCode === '1' || this.query.functionCode === '2' || this.query.functionCode === '5') {
                return 'digital'
            } else if(this.query.functionCode === '3' || this.query.functionCode === '4' || this.query.functionCode === '6') {
                return 'analog'
            }
        },
        alertFlagOptions() {
            if(this.registerType === 'digital') {
                // when register is digital
                return [
                    {
                        text: 'Alert on 0',
                        value: '1'
                    },
                    {
                        text: 'Alert on 1',
                        value: '2'
                    },
                    {
                        text: 'Alert on change',
                        value: '3'
                    },
                    {
                        text: 'Disable Alert',
                        value: '4'
                    }
                ]
            } else if(this.registerType === 'analog') {
                // when register is analog
                return [
                    {
                        text: 'Alert on both minimum and maximum thresholds',
                        value: '1'
                    },
                    {
                        text: 'Alert on minimum threshold only',
                        value: '2'
                    },
                    {
                        text: 'Alert on maximum threshold only',
                        value: '3'
                    },
                    {
                        text: 'Disable Alert',
                        value: '4'
                    }
                ]
            }
        },
        datatypeOptions() {
            if(this.registerType === 'digital') {
                return [
                    {
                        text: 'Boolean',
                        value: '1'
                    }
                ]
            } else if(this.registerType === 'analog') {
                return [
                    {
                        text: 'Signed Integer',
                        value: '2'
                    },
                    {
                        text: 'Unsigned Integer',
                        value: '3'
                    },
                    {
                        text: 'Float',
                        value: '4'
                    },
                    {
                        text: 'Long Signed Integer',
                        value: '5'
                    },
                    {
                        text: 'Dword Unsigned Long',
                        value: '6'
                    },
                    {
                        text: 'Double',
                        value: '7'
                    },
                    {
                        text: 'Dword Reverse (unsigned long reverse)',
                        value: '8'
                    },
                    {
                        text: 'Long Reverse (signed long reverse)',
                        value: '9'
                    },
                    {
                        text: 'Float Reverse',
                        value: '10'
                    },
                    {
                        text: 'BCD (2 bytes)',
                        value: '11'
                    },
                    {
                        text: 'BCD (4 bytes)',
                        value: '12'
                    },
                    {
                        text: 'Reverse BCD (4 bytes)',
                        value: '13'
                    },
                    {
                        text: 'Double Reverse (8 bytes)',
                        value: '14'
                    }
                ]
            }
        },
        maxTagOffsetValue() {
            if(this.registerType === 'digital') {
                return this.query.numberOfRegisters - this.tag.tagAddressLength
            } else if(this.registerType === 'analog') {
                return (this.query.numberOfRegisters * 2) - this.tag.tagAddressLength
            }
        }
    },
    props: ['query', 'tag', 'loggingMethod'],
    mounted() {
        this.tag.tagName = `Tag${this.tag.tagId}`
    },
    methods: {
        suggestTagAddressLength() {
            if(this.tag.tagDatatype === '1') {
                this.tag.tagAddressLength = '1'
            } else if(this.tag.tagDatatype === '2' || this.tag.tagDatatype === '3' || this.tag.tagDatatype === '11') {
                this.tag.tagAddressLength = '2'
            } else if(this.tag.tagDatatype === '4' || this.tag.tagDatatype === '5' || this.tag.tagDatatype === '6' || this.tag.tagDatatype === '8' || this.tag.tagDatatype === '9' || this.tag.tagDatatype === '10' || this.tag.tagDatatype === '12' || this.tag.tagDatatype === '13') {
                this.tag.tagAddressLength = '4'
            } else if(this.tag.tagDatatype === '7' || this.tag.tagDatatype === '14') {
                this.tag.tagAddressLength = '8'
            }
            this.validateTagAddressOffset()
        },
        validateTagName() {
            if(/^[a-zA-Z]{1}[a-zA-Z0-9]{0,14}$/.test(this.tag.tagName)) {
                this.tagNameError = false
            } else {
                this.tagNameError = true
            }
        },
        validateTagAddressOffset() {
            if(this.tag.tagDatatype === '') {
                this.tagDatatypeError = true
            } else {
                this.tagDatatypeError = false
            }
            if(/^[0-9]+$/.test(this.tag.tagAddressOffset) && (parseInt(this.tag.tagAddressOffset) >= 0 && parseInt(this.tag.tagAddressOffset) <= this.maxTagOffsetValue)) {
                this.tagAddressOffsetError = false
            } else {
                this.tagAddressOffsetError = true
            }
        },
        validateTagAddressLength() {
            if(/^[0-9]+$/.test(this.tag.tagAddressLength) && (parseInt(this.tag.tagAddressLength) > 0 && parseInt(this.tag.tagAddressLength) < 9)) {
                this.tagAddressLengthError = false
            } else {
                this.tagAddressLengthError = true
            }
        },
        validateTagLinearScalingCoefficientA() {
            if(parseFloat(this.tag.tagLinearScalingCoefficientA) && parseFloat(this.tag.tagLinearScalingCoefficientA) !== 0) {
                this.tagLinearScalingCoefficientAError = false
            } else {
                this.tagLinearScalingCoefficientAError = true
            }
        },
        validateTagLinearScalingOffsetB() {
            if(parseFloat(this.tag.tagLinearScalingOffsetB)) {
                this.tagLinearScalingOffsetBError = false
            } else {
                this.tagLinearScalingOffsetBError = true
            }
        },
        validateTagMinimumThreshold() {
            if(/\d+/.test(this.tag.tagMinimumThreshold)) {
                this.tagMinimumThresholdError = false
            } else {
                this.tagMinimumThresholdError = true
            }
        },
        validateTagMaximumThreshold() {
            if(/\d+/.test(this.tag.tagMaximumThreshold)) {
                this.tagMaximumThresholdError = false
            } else {
                this.tagMaximumThresholdError = true
            }
        },
        validateTagMobileNumber() {
            if(/\d+/.test(this.tag.tagMobileNumber) || this.tag.tagMobileNumber === 'NULL') {
                this.tagMobileNumberError = false
            } else {
                this.tagMobileNumberError = true
            }
        },
        removeTag() {
            this.$emit('tagRemoved', { tagId: this.tag.tagId })
        }
    }
}
</script>



// WEBPACK FOOTER //
// src/components/polling-group/Tag.vue